<form
  [formGroup]="dataService.formGroupSettings"
  *transloco="let t"
  class="flex flex-col gap-6"
  role="form"
>
  <div class="flex items-center gap-4">
    <label
      class="flex shrink-0 items-center text-base font-semibold text-blue-dark"
      for="trainingName"
    >
      {{ 'portal.trainings.setup.configuration.trainingName' | transloco }}
    </label>
    <app-input-field
      id="trainingName"
      [control]="dataService.formGroupSettings.controls.learningPlanName"
      [placeholder]="
        t('portal.trainings.setup.configuration.trainingNamePlaceholder')
      "
      [bottomMargin]="false"
      [hideErrors]="true"
      class="w-full"
    />
    @if (isCustomTraining && !isPrecuratedTraining) {
      <label
        for="topicArea"
        class="flex shrink-0 items-center text-base font-semibold text-blue-dark"
      >
        {{
          t('portal.trainings.builder.stepper.settings.form.topicArea.label')
        }}
      </label>
      <app-select
        id="topicArea"
        [control]="dataService.formGroupSettings.controls.topicAreaId"
        [options]="topicAreasWithDisabled"
        [bottomMargin]="false"
        [translocoPrefix]="'portal.trainings.builder.stepper.settings.form.topicArea.options'"
        [lockReason]="
          hasCoursesSelected
            ? t(
                'portal.trainings.builder.stepper.settings.form.topicArea.lockedWithCourses'
              )
            : ''
        "
      />
    }
  </div>

  <p class="text-base font-light text-grey">
    {{ t('portal.trainings.setup.configuration.description') }}
  </p>

  <div
    class="flex flex-col gap-4 rounded-xl border border-grey-lightGrey bg-grey-veryLightGrey p-4"
  >
    <div class="text-sm font-medium text-blue-dark">
      {{ t('portal.trainings.builder.stepper.settings.form.deadline.type') }}
    </div>

    <div
      class="flex flex-col gap-4 rounded-lg border p-4 transition"
      [ngClass]="{
        'border-2 border-blue bg-blue-veryLight':
          dataService.formGroupSettings.controls.deadlineType.value ===
          'period',
        'border-grey-lightGrey bg-white':
          dataService.formGroupSettings.controls.deadlineType.value !==
          'period',
      }"
    >
      <label class="flex cursor-pointer items-start gap-3">
        <input
          type="radio"
          value="period"
          formControlName="deadlineType"
          class="mt-1 size-5 shrink-0 text-blue accent-blue"
        />
        <div class="flex flex-col">
          <span class="font-medium text-blue-dark">
            {{
              t(
                'portal.trainings.builder.stepper.settings.form.deadline.period.label'
              )
            }}
          </span>
          <span class="text-sm font-light text-grey">
            {{
              t(
                'portal.trainings.builder.stepper.settings.form.deadline.period.help'
              )
            }}
          </span>
        </div>
      </label>

      @if (
        dataService.formGroupSettings.controls.deadlineType.value === 'period'
      ) {
        <mat-divider
          class="material-divider w-full border-t-2 !text-grey-midGrey"
        ></mat-divider>

        <div class="flex flex-col gap-4 lg:flex-row lg:gap-6">
          <div class="flex w-full flex-col gap-2 lg:basis-1/2">
            <label
              class="flex items-center gap-1 text-sm font-medium text-blue-dark"
              for="startDate"
            >
              {{ t('portal.trainings.setup.configuration.startDate') }}
              <mat-icon
                class="hvs-icon-xs text-grey-midGrey"
                [matTooltip]="
                  t('portal.trainings.setup.configuration.startDateTooltip')
                "
                matTooltipPosition="above"
                matTooltipClass="tooltip-above"
              >
                help_outline
              </mat-icon>
            </label>
            <app-datepicker
              [control]="dataService.formGroupSettings.controls.startDate"
              [minDate]="dataService.minDate"
              [maxDate]="dataService.maxDate"
              [hideErrors]="true"
              [bottomMargin]="false"
            />
            <p class="text-sm font-light text-grey">
              {{ t('portal.trainings.setup.configuration.startDateHint') }}
            </p>
          </div>

          <div class="flex w-full flex-col gap-2 lg:basis-1/2">
            <label class="text-sm font-medium text-blue-dark" for="duration">
              {{ t('portal.trainings.setup.configuration.duration') }}
            </label>
            <app-select
              [control]="dataService.formGroupSettings.controls.duration"
              [translocoPrefix]="
                isCustomTraining
                  ? 'portal.trainings.setup.configuration.durationOptions'
                  : 'portal.trainings.setup.configuration.durationOptionsSingleCourse'
              "
              [options]="dataService.durationOptions"
              [bottomMargin]="false"
            />
            <p class="text-sm font-light text-grey">
              {{ t('portal.trainings.setup.configuration.durationHint') }}
            </p>
          </div>
        </div>
      }
    </div>

    @if (!isCustomTraining) {
      <div
        class="flex flex-col gap-4 rounded-lg border p-4 transition"
        [ngClass]="{
          'border-2 border-blue bg-blue-veryLight':
            dataService.formGroupSettings.controls.deadlineType.value ===
            'fixed',
          'border-grey-lightGrey bg-white':
            dataService.formGroupSettings.controls.deadlineType.value !==
            'fixed',
        }"
      >
        <label class="flex cursor-pointer items-start gap-3">
          <input
            type="radio"
            value="fixed"
            formControlName="deadlineType"
            class="mt-1 size-5 shrink-0 text-blue accent-blue"
          />
          <div class="flex flex-col">
            <span class="font-medium text-blue-dark">
              {{
                t(
                  'portal.trainings.builder.stepper.settings.form.deadline.fixed.label'
                )
              }}
            </span>
            <span class="text-sm font-light text-grey">
              {{
                t(
                  'portal.trainings.builder.stepper.settings.form.deadline.fixed.help'
                )
              }}
            </span>
          </div>
        </label>

        @if (
          dataService.formGroupSettings.controls.deadlineType.value === 'fixed'
        ) {
          <mat-divider
            class="material-divider w-full border-t-2 !text-grey-midGrey"
          ></mat-divider>

          <div class="flex flex-col gap-4 lg:flex-row lg:gap-6">
            <div class="flex w-full flex-col gap-2 lg:basis-1/2">
              <label class="text-sm font-medium text-blue-dark" for="startDate">
                {{ t('portal.trainings.setup.configuration.startDate') }}
              </label>
              <app-datepicker
                [control]="dataService.formGroupSettings.controls.startDate"
                [minDate]="dataService.minDate"
                [maxDate]="dataService.maxDate"
                [hideErrors]="true"
                [bottomMargin]="false"
                [enableDaySelection]="true"
              />
              <p class="text-sm font-light text-grey">
                {{ t('portal.trainings.setup.configuration.startDateHint') }}
              </p>
            </div>

            <div class="flex w-full flex-col gap-2 lg:basis-1/2">
              <label class="text-sm font-medium text-blue-dark" for="endDate">
                {{
                  t(
                    'portal.trainings.builder.stepper.settings.form.endDate.label'
                  )
                }}
              </label>
              <app-datepicker
                id="endDate"
                [control]="dataService.formGroupSettings.controls.endDate"
                [minDate]="dataService.minEndDate"
                [maxDate]="dataService.maxDate"
                [hideErrors]="true"
                [bottomMargin]="false"
                [enableDaySelection]="true"
              />
              <p class="text-sm font-light text-grey">
                {{
                  t(
                    'portal.trainings.builder.stepper.settings.form.endDate.help'
                  )
                }}
              </p>
            </div>
          </div>
        }
      </div>
    }
  </div>
  @if (isCustomTraining || isPrecuratedTraining) {
    <app-learning-plan-builder-course-selection
      [isEditable]="!isPrecuratedTraining"
      [isPrecuratedTraining]="isPrecuratedTraining"
    ></app-learning-plan-builder-course-selection>
  }
</form>
